<template>
  <b-container class="section-p70">
    <b-row>
      <b-col cols="auto">
        <h2 class="title-blue">{{ t("SEARCH_QUERY") }}</h2>
      </b-col>
    </b-row>

    <b-row class="mt-3 justify-content-between align-items-center">
      <b-form class="col" @submit="onSearch">
        <b-row class="align-items-center">
          <b-col xl="4" class="mb-2" v-if="roleSearchOptions.length > 1">
            <multiselect v-model="searchSelectedRoles"
                         :custom-label="t"
                         :options="roleSearchOptions"
                         :placeholder="t('ROLE')"
                         :select-label="t('SELECT')"
                         :selected-label="t('SELECTED')"
                         :deselect-label="t('REMOVE')"
                         :multiple="true"
            >
              <template #tag="{option, remove}">
                <span class="multiselect__tag">
                  <span>{{ t(option) }}</span>
                  <i class="multiselect__tag-icon" @click="remove(option)"/>
                </span>
              </template>
            </multiselect>
          </b-col>

          <b-col xl="4" class="mb-2">
            <b-input type="text" v-model="searchValue" :placeholder="t('SEARCH')"/>
          </b-col>

          <b-col xl="4" class="mb-2">
            <b-button type="submit" variant="blue">{{ t("SEARCH") }}</b-button>
          </b-col>
        </b-row>
      </b-form>

      <b-col class="flex-grow-0">
        <b-dropdown variant="blue" :text="t('ACTIONS')">
          <b-dropdown-item v-if="auth.hasRole(Role.ADMIN)" @click="openAddAccountModal" class="text-nowrap">
            {{ t("REGISTER_NEW_USER") }}
          </b-dropdown-item>
          <b-dropdown-item @click="downloadAccountsExcel">
            {{ t("DOWNLOAD_FILE") }}&nbsp;(Excel)
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
    </b-row>
    <div class="separator-line mt-3"></div>
    <div class="row">
      <div class="col">
        <PaginatedTable :fields="fields" :on-page-change="search" ref="paginatedTableRef">
          <template v-slot:cell(name)="{ item }">
            <span class="mr-2" v-if="item.firstName || item.lastName">
              {{ item.firstName }} {{ item.lastName }}
            </span>

            <b-badge v-if="item.isArchived" class="mr-2" variant="warning">
              {{ t("ARCHIVED") }}
            </b-badge>
            <b-badge v-if="item.activeTill" class="mr-2" variant="warning">
              {{ t("ACTIVE_TILL") }}: {{ dateFormat(item.activeTill) }}
            </b-badge>
            <b-badge v-if="!item.isActivated" class="mr-2" variant="info">
              {{ t("NOT_ACTIVATED") }}
            </b-badge>
          </template>
          <template v-slot:cell(view)="{ item }">
            <div>
              <b-button
                  :disabled="item.isArchived === true && !auth.canSeeArchivedAccounts()"
                  :to="{
                  name: 'Trainee',
                  params: { id: `${item.id}`, isArchived: item.isArchived },
                }"
              >
                {{ t("TRAINING_CARD") }}
              </b-button>
            </div>

          </template>
          <template v-slot:cell(see)="{ item }">
            <div>
              <b-button
                  :disabled="item.role !== 'TRAINEE'"
                  :to="{
                  name: 'Groups',
                  params: { studentId: `${item.id}` },
                }"
              >
                <b-icon-eye-fill></b-icon-eye-fill>
              </b-button>
            </div>

          </template>
          <template v-slot:cell(role)="{ item }">
            <div class="input-group input-group-sm" v-if="auth.hasRole(Role.ADMIN)" style="min-width: 250px">
              <b-select v-model="item.role" class="border-0 no-shadow p-1">
                <option
                    v-for="option in roleOptions"
                    :key="option.id"
                    :value="option.value"
                >
                  {{ option.text }}
                </option>
              </b-select>

              <b-button
                  style="border-radius: 0"
                  variant="blue"
                  @click="changeRole(item.role, item.ssn)"
              >
                {{ t("SAVE") }}
              </b-button>
            </div>
            <span v-else>
              {{ t(item.role) }}
            </span>
          </template>
        </PaginatedTable>
      </div>
    </div>
  </b-container>

  <AddAccountModal id="add-account-modal"
                   ref="addAccountModalRef"
                   :after-save="resetSearchResults"/>

  <loader-container :active="downloadingExcel"/>
</template>

<script setup>
import {computed, inject, nextTick, ref} from "vue";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";
import {Role, Role as $roles} from "@/auth/oauth";
import PaginatedTable from "@/views/components/PaginatedTable.vue";
import AddAccountModal from "@/views/components/modals/AddAccountModal.vue";
import {useDateFormat} from "@/mixins/dateFormatMixin2";
import multiselect from 'vue-multiselect';
import LoaderContainer from "@/components/LoaderContainer.vue";
import {makeSuccessToast} from "@/util/toastUtils";

const route = useRoute();
const { t, locale } = useI18n();
const auth = inject("auth");
const axios = inject("axios");
const {dateFormat} = useDateFormat();

const roleOptions = computed(() => {
  return [
    { value: $roles.TRAINEE, text: t("TRAINEE") },
    { value: $roles.TRAINER, text: t("TRAINER") },
    { value: $roles.ADMIN, text: t("ADMIN") },
    { value: $roles.CONTENT_MANAGER, text: t("CONTENT_MANAGER") },
    { value: $roles.SCHOOL_MANAGER, text: t("SCHOOL_MANAGER") },
    { value: $roles.VIEWER, text: t("VIEWER") },
    { value: $roles.DISABLED_USER, text: t("DISABLED_USER") },
  ];
});

const roleSearchOptions = computed(() => {
  if (auth.hasRole(Role.ADMIN)) {
    return [Role.TRAINEE, Role.TRAINER, Role.ADMIN, Role.SCHOOL_MANAGER, Role.CONTENT_MANAGER, Role.VIEWER, Role.DISABLED_USER];
  }

  if (auth.hasRole(Role.VIEWER)) {
    return [Role.TRAINEE, Role.TRAINER];
  }

  if (auth.hasSomeRole(Role.TRAINER, Role.SCHOOL_MANAGER)) {
    return [Role.TRAINEE];
  }

  return [];
});

const paginatedTableRef = ref(null);
const addAccountModalRef = ref(null);

const searchSelectedRoles = ref([]);
const searchValue = ref(null);

const downloadingExcel = ref(false);

const changeRole = async (role, ssn) => {
  try {
    const resp = await axios.patch("/account/role", { role: role, ssn: ssn });
    if (resp.data && resp) {
      makeSuccessToast(`${t("SAVED")} - ${t(resp.data.role)}`);
    }
  } catch (e) {
    console.log(e);
  }
};

const onSearch = async (ev) => {
  ev.preventDefault();
  await paginatedTableRef.value.onSearch();
};

const resetSearchResults = async () => {
  searchValue.value = null;
  searchSelectedRoles.value = [];
  await paginatedTableRef.value.reset();
};

const getSearchParams = () => {
  return {
    "searchValue": searchValue.value,
    "roles": (searchSelectedRoles.value.length ? searchSelectedRoles.value : roleSearchOptions.value).join(","),
    "fields": "SSN,NAME,EMAIL,PHONE",
  };
};

const search = async (params) => {
  const resp = await axios.post("/account/search/all", {}, {params: {...params, ...getSearchParams()}});
  await nextTick();
  return resp;
};

const downloadAccountsExcel = async () => {
  downloadingExcel.value = true;

  try {
    const resp = await axios.get("/account/search/all/excel", {params: getSearchParams(), responseType: "blob"});
    const blob = new Blob([resp.data]);
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = "accounts.xlsx";
    link.click();

    URL.revokeObjectURL(link.href);
  } finally {
    downloadingExcel.value = false;
  }

  // Refresh data in UI just to keep the results same in UI and downloaded Excel file.
  await paginatedTableRef.value.onSearch();
};

const fields = computed(() => {
  return [
    {key: "name", label: t("NAME"), sortable: true},
    {key: "ssn", label: t("SSN"), sortable: true},
    {key: "email", label: t("EMAIL"), sortable: true},
    {key: "phone", label: t("PHONE"), sortable: true},
    {key: "role", label: t("ROLE"), sortable: false},
    {key: "see", label: "", sortable: false},
    {key: "view", label: "", sortable: false},
  ];
});

const openAddAccountModal = () => {
  addAccountModalRef.value.openModal();
};
</script>
